import { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Divider,
  Heading,
  HStack,
  Show,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { countriesInfo } from '@/models/country';
import { DISPLAY_POINTS_SYSTEM, RULES_SCORE_EXAMPLE_LIST, RULES_SCORE_EXAMPLE_TOTAL } from '@/constants/displayPointsSystem';
import { Flag } from '@/components/Flag';
import { RulesMatchdaysTab } from './RulesMatchdaysTab';
import { RulesModalTournamentSummary } from './RulesModalTournamentSummary';
import { RulesRankTeamsDemo } from './RulesRankTeamsDemo';

export function RulesPageContent(props?: {
  prompt?: boolean;
  onNext?: () => void;
  embeddedInModal?: boolean;
  tabsAtBottom?: boolean;
  showTournamentSummary?: boolean;
}) {
  const [tabIndex, setTabIndex] = useState(0);

  const scrollTableContainerProps = {
    w: 'full' as const,
    minW: 0,
    overflowX: 'auto' as const,
    sx: { WebkitOverflowScrolling: 'touch' },
  };

  const pointsTable = (
    <TableContainer {...scrollTableContainerProps}>
      <Table variant='simple' size='sm'>
        <Thead>
          <Tr>
            <Th />
            <Th isNumeric>Points Earned</Th>
          </Tr>
        </Thead>
        <Tbody>
          {DISPLAY_POINTS_SYSTEM.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item[0]}</Td>
              <Td isNumeric>{item[1]}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const exampleListTable = (
    <TableContainer {...scrollTableContainerProps}>
      <Table variant='simple' size='sm' minW='20rem'>
        <Thead>
          <Tr>
            <Th>Country List</Th>
            <Th isNumeric>Points</Th>
            <Th isNumeric>Multiplier</Th>
            <Th isNumeric>Weighted Points</Th>
          </Tr>
        </Thead>
        <Tbody>
          {RULES_SCORE_EXAMPLE_LIST.map((item, idx) => (
            <Tr key={idx}>
              <Td py={2}>
                <HStack spacing={3} align='center' w='full'>
                  <Text w='6' flexShrink={0} textAlign='center' fontWeight='semibold'>
                    {idx + 1}
                  </Text>
                  <Text flex={1} minW={0}>
                    {countriesInfo[item[0]].name}
                  </Text>
                  <Center w='10' flexShrink={0}>
                    <Flag country={item[0]} w={6} borderRadius='3px' svg />
                  </Center>
                </HStack>
              </Td>
              <Td isNumeric>{item[1]}</Td>
              <Td isNumeric>{item[2]}</Td>
              <Td isNumeric>{item[3]}</Td>
            </Tr>
          ))}
        </Tbody>
        <Tfoot>
          <Tr>
            <Td />
            <Td />
            <Td>Total Points</Td>
            <Td isNumeric>{RULES_SCORE_EXAMPLE_TOTAL}</Td>
          </Tr>
        </Tfoot>
      </Table>
    </TableContainer>
  );

  const dividerColor = useColorModeValue('black', 'white');
  const tabsAtBottom = props?.tabsAtBottom ?? false;

  const tabList = (
    <TabList flexWrap='wrap' gap={1} justifyContent='center' pt={tabsAtBottom ? 4 : 0}>
      <Tab fontSize={{ base: 'sm', md: 'md' }}>Rank Your Teams</Tab>
      <Tab fontSize={{ base: 'sm', md: 'md' }}>Score Points</Tab>
      <Tab fontSize={{ base: 'sm', md: 'md' }}>Matchday</Tab>
    </TabList>
  );

  const tabPanels = (
        <TabPanels>
          <TabPanel px={0} pt={4}>
            <VStack spacing={4} align='center' w='full' pt={2}>
              <Heading textAlign='center' size={{ base: 'sm', md: 'md' }} w='full' flexShrink={0}>
                Rank Your Teams
              </Heading>
              <Box w='full' mt={1}>
                {tabIndex === 0 ? <RulesRankTeamsDemo embeddedInModal={props?.embeddedInModal} /> : null}
              </Box>
            </VStack>
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <VStack spacing={6} align='center' w='full'>
              <Heading textAlign='center' size={{ base: 'sm', md: 'md' }} w='full'>
                Score Points
              </Heading>
              <Show above='lg'>
                <HStack align='start' spacing={4} w='full' justify='center'>
                  {pointsTable}
                  <Box px={2}>
                    <Divider orientation='vertical' height='300px' borderColor={dividerColor} />
                  </Box>
                  {exampleListTable}
                </HStack>
              </Show>
              <Show below='lg'>
                <VStack spacing={4} w='full'>
                  {pointsTable}
                  {exampleListTable}
                </VStack>
              </Show>
            </VStack>
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <VStack spacing={4} align='center' w='full'>
              <Heading textAlign='center' size={{ base: 'sm', md: 'md' }} w='full'>
                Matchday &amp; Penalties
              </Heading>
              <Box w='full'>{tabIndex === 2 ? <RulesMatchdaysTab /> : null}</Box>
            </VStack>
          </TabPanel>
        </TabPanels>
  );

  return (
    <VStack align='stretch' spacing={6} w='full'>
      {props?.showTournamentSummary ? <RulesModalTournamentSummary /> : null}
      <Tabs index={tabIndex} onChange={setTabIndex} variant='line' colorScheme='blue' w='full'>
        {tabsAtBottom ? (
          <>
            {tabPanels}
            {tabList}
          </>
        ) : (
          <>
            {tabList}
            {tabPanels}
          </>
        )}
      </Tabs>
      {props?.prompt ? (
        <Button colorScheme='green' alignSelf='flex-end' onClick={() => props?.onNext?.()}>
          Pick Your Teams
        </Button>
      ) : null}
    </VStack>
  );
}
